import { useEffect, useRef } from "react";
import bottle from "../../assets/images/careveli-vitamin-c-serum.jpg";

/**
 * "A world within" login stage.
 * One CSS timeline (--cv-loop) drives the whole film: the camera pushes into
 * the label, opens onto a Vitamin C atrium, flies through the light, lands
 * a serum drop, then pulls back out to the bottle. JS only feeds the pointer
 * parallax (--mx / --my); touch and reduced-motion skip it.
 */

const polar = (r, deg) => {
  const a = ((deg - 90) * Math.PI) / 180;
  return `${(50 + r * Math.cos(a)).toFixed(2)} ${(50 + r * Math.sin(a)).toFixed(2)}`;
};

// Ten pulp wedges with a thin pith gap, plus two juice-cell highlights per wedge.
const WEDGES = Array.from({ length: 10 }, (_, i) => {
  const a1 = i * 36 + 2.6;
  const a2 = (i + 1) * 36 - 2.6;
  const mid = i * 36 + 18;
  return {
    d: `M ${polar(6, a1)} L ${polar(40, a1)} A 40 40 0 0 1 ${polar(40, a2)} L ${polar(6, a2)} Z`,
    cells: [
      { c: polar(30, mid - 6), rot: mid },
      { c: polar(20, mid + 5), rot: mid },
    ],
  };
});

const PILLARS = [
  { left: "4%", width: "7%" },
  { left: "19%", width: "11%" },
  { left: "44%", width: "12%" },
  { left: "70%", width: "11%" },
  { left: "89%", width: "7%" },
];

const SLICES = [
  { x: "16%", y: "9%", s: "21%", d: 18, b: "0px", t: "11s", r0: "-8deg", r1: "16deg", dx: "14px", dy: "-18px" },
  { x: "64%", y: "54%", s: "22%", d: 24, b: "0px", t: "9s", r0: "12deg", r1: "-14deg", dx: "-12px", dy: "-22px" },
  { x: "72%", y: "8%", s: "12%", d: 8, b: "3px", t: "13s", r0: "0deg", r1: "40deg", dx: "-8px", dy: "12px" },
  { x: "4%", y: "64%", s: "11%", d: 6, b: "4px", t: "12s", r0: "20deg", r1: "-20deg", dx: "10px", dy: "-10px" },
  { x: "40%", y: "80%", s: "8%", d: 4, b: "6px", t: "10s", r0: "0deg", r1: "30deg", dx: "6px", dy: "-8px" },
];

const LEAVES = [
  { x: "54%", y: "14%", s: "14%", d: 14, b: "1px", t: "8s", r0: "-36deg", r1: "-14deg", dx: "10px", dy: "-12px" },
  { x: "24%", y: "58%", s: "12%", d: 20, b: "0px", t: "10s", r0: "150deg", r1: "172deg", dx: "-10px", dy: "-16px" },
];

const BUBBLES = Array.from({ length: 12 }, (_, i) => ({
  x: `${((i * 37) % 92) + 4}%`,
  s: `${4 + ((i * 7) % 9)}px`,
  t: `${6 + ((i * 5) % 7)}s`,
  delay: `-${(i * 1.3).toFixed(1)}s`,
  dx: `${(i % 2 ? 1 : -1) * (8 + ((i * 3) % 14))}px`,
}));

const SCENES = [
  { no: "01", label: "The serum" },
  { no: "02", label: "Inside — pure Vitamin C" },
  { no: "03", label: "Light, bottled" },
  { no: "04", label: "One drop" },
];

const itemStyle = item => ({
  "--x": item.x,
  "--y": item.y,
  "--s": item.s,
  "--d": item.d,
  "--b": item.b,
  "--t": item.t,
  "--r0": item.r0,
  "--r1": item.r1,
  "--dx": item.dx,
  "--dy": item.dy,
});

function OrangeSlice() {
  return (
    <svg viewBox="0 0 100 100">
      <circle cx="51.5" cy="53" r="47.5" fill="#9c4a0c" />
      <circle cx="50" cy="50" r="47.5" fill="url(#cv-rind)" />
      <circle cx="50" cy="50" r="44" fill="#fbe8c4" />
      {WEDGES.map(w => (
        <g key={w.d}>
          <path d={w.d} fill="url(#cv-pulp)" stroke="#f6cf8a" strokeWidth="0.6" strokeLinejoin="round" />
          {w.cells.map(cell => {
            const [cx, cy] = cell.c.split(" ");
            return (
              <ellipse
                key={cell.c}
                cx={cx}
                cy={cy}
                rx="1.4"
                ry="4.2"
                fill="#fff6dc"
                opacity="0.38"
                transform={`rotate(${cell.rot} ${cx} ${cy})`}
              />
            );
          })}
        </g>
      ))}
      <circle cx="50" cy="50" r="5" fill="#fbe8c4" />
      <ellipse cx="34" cy="26" rx="18" ry="7" fill="#fff" opacity="0.16" transform="rotate(-32 34 26)" />
    </svg>
  );
}

function Leaf() {
  return (
    <svg viewBox="0 0 100 100">
      <path d="M8 52 C 26 18, 70 10, 94 46 C 72 84, 28 88, 8 52 Z" fill="url(#cv-leaf)" />
      <path d="M12 52 C 40 46, 66 44, 90 47" stroke="#e8f2c8" strokeOpacity="0.55" strokeWidth="1.6" fill="none" />
    </svg>
  );
}

export default function CareveliProductHero() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
    const desktop = window.matchMedia("(min-width: 1024px)");
    if (reduce.matches || !fine.matches) return undefined;

    const target = { x: 0, y: 0 };
    const current = { x: 0, y: 0 };
    const clamp = v => Math.max(-1, Math.min(1, v));
    let frame = 0;
    let running = false;

    const tick = () => {
      current.x += (target.x - current.x) * 0.08;
      current.y += (target.y - current.y) * 0.08;
      root.style.setProperty("--mx", current.x.toFixed(3));
      root.style.setProperty("--my", current.y.toFixed(3));
      if (Math.abs(target.x - current.x) + Math.abs(target.y - current.y) < 0.002) {
        running = false;
        return;
      }
      frame = window.requestAnimationFrame(tick);
    };

    const start = () => {
      if (running) return;
      running = true;
      frame = window.requestAnimationFrame(tick);
    };

    const onMove = event => {
      if (!desktop.matches) return;
      const rect = root.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      target.x = clamp(((event.clientX - rect.left) / rect.width - 0.5) * 2);
      target.y = clamp(((event.clientY - rect.top) / rect.height - 0.5) * 2);
      start();
    };

    const onLeave = () => {
      target.x = 0;
      target.y = 0;
      start();
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("blur", onLeave);
    document.documentElement.addEventListener("mouseleave", onLeave);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("blur", onLeave);
      document.documentElement.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <div className="cv-stage" ref={rootRef}>
      <svg className="cv-defs" aria-hidden="true" focusable="false">
        <defs>
          <radialGradient id="cv-rind" cx="38%" cy="34%" r="70%">
            <stop offset="0%" stopColor="#f6b04e" />
            <stop offset="70%" stopColor="#de7a16" />
            <stop offset="100%" stopColor="#b85a0c" />
          </radialGradient>
          <radialGradient id="cv-pulp" cx="50" cy="50" r="42" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#ffe4a3" />
            <stop offset="45%" stopColor="#f9b84a" />
            <stop offset="100%" stopColor="#e88b1f" />
          </radialGradient>
          <linearGradient id="cv-leaf" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#8fa65a" />
            <stop offset="100%" stopColor="#3f5826" />
          </linearGradient>
          <linearGradient id="cv-drop" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fff4c9" />
            <stop offset="40%" stopColor="#ffc34d" />
            <stop offset="100%" stopColor="#d97a0a" />
          </linearGradient>
        </defs>
      </svg>

      <div className="cv-stage__bottle-wrap">
        <img
          className="cv-stage__bottle"
          src={bottle}
          alt="Careveli 15% Vitamin C Face Serum"
          draggable="false"
        />
      </div>

      <div className="cv-world" aria-hidden="true">
        <div className="cv-world__dolly">
          <div className="cv-world__atrium">
            {PILLARS.map(p => (
              <span key={p.left} style={{ left: p.left, width: p.width }} />
            ))}
          </div>
          <div className="cv-world__floor" />
          <div className="cv-world__rays" />
          <div className="cv-world__core" />
          <div className="cv-world__ingredients">
            {SLICES.map(item => (
              <div className="cv-item" style={itemStyle(item)} key={`slice-${item.x}-${item.y}`}>
                <OrangeSlice />
              </div>
            ))}
            {LEAVES.map(item => (
              <div className="cv-item cv-item--leaf" style={itemStyle(item)} key={`leaf-${item.x}-${item.y}`}>
                <Leaf />
              </div>
            ))}
          </div>
          <div className="cv-world__bubbles">
            {BUBBLES.map(b => (
              <span
                key={`${b.x}-${b.delay}`}
                style={{ "--x": b.x, "--s": b.s, "--t": b.t, "--delay": b.delay, "--dx": b.dx }}
              />
            ))}
          </div>
          <div className="cv-world__drop">
            <svg viewBox="0 0 100 130">
              <path d="M50 2 C 58 26, 92 58, 92 86 A 42 42 0 0 1 8 86 C 8 58, 42 26, 50 2 Z" fill="url(#cv-drop)" />
              <ellipse cx="34" cy="80" rx="9" ry="16" fill="#fff" opacity="0.55" transform="rotate(18 34 80)" />
            </svg>
            <span className="cv-world__ripple" />
          </div>
        </div>
      </div>

      <div className="cv-stage__flash" aria-hidden="true" />

      <div className="cv-stage__ui">
        <div className="cv-stage__top">
          <span>The Vitamin C Edit</span>
          <span>No. 01</span>
        </div>
        <div className="cv-stage__headline">
          <p className="cv-stage__title">
            Light,
            <br />
            decoded.
          </p>
          <span className="cv-stage__rule" />
          <p className="cv-stage__meta">15% Vitamin C · pH 4.5</p>
        </div>
        <div className="cv-stage__rail" aria-hidden="true">
          <span />
        </div>
        <div className="cv-stage__foot" aria-hidden="true">
          <div className="cv-stage__captions">
            {SCENES.map(scene => (
              <span key={scene.no}>
                <b>{scene.no}</b>
                {scene.label}
              </span>
            ))}
          </div>
          <div className="cv-stage__dots">
            {SCENES.map(scene => (
              <i key={scene.no} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
